import { Injectable } from '@angular/core';
import { DocumentPricesService } from './document-prices.service';
import {
    DiscountsAndMultipliersService,
    PriceVersion,
    ProductsList,
    SalesDocument,
    SalesDocumentPosition,
    State,
    TaxRatesService,
} from '@icc/b2b/shared';
import { ConfigService, PricesDataService, TranslateService } from '@icc/shared';
import { Subject, distinctUntilChanged, distinctUntilKeyChanged, tap } from 'rxjs';
import { PriceVersionService } from './price-version-selector/price-version.service';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';
import { ToastService, core, deepCompare, parseDate } from '@icc/shared/helpers';
import { DocumentManagerService } from './document-manager.service';
import { OfferValidityService } from './offer-validity/offer-validity.service';
import { PriceBaseService2 } from '@icc/price2/b2b';
import {
    changePosition,
    changePositionBasePrice,
    changePositionPrice,
} from './+state/sales-documents.actions';
import { PositionService } from './position.service';
import { getSelectedSales } from './+state/sales-documents.selectors';
import { addMinutes } from 'date-fns';
import { OwnProductsService } from './own-products.service';
import { ConfiguratorLoaderService } from '@icc/b2b/configurator';

@Injectable({
    providedIn: 'root',
})
export class B2bDocumentPricesService extends DocumentPricesService {
    priceUpdatePending$ = new Subject<boolean>();
    activePriceVersion: PriceVersion;

    private priceUpdatePending = false;
    private openedDocument: SalesDocument | null = null;

    constructor(
        private configService: ConfigService,
        private priceVersionService: PriceVersionService,
        private pricesDataService: PricesDataService,
        private store: Store<State>,
        private http: HttpClient,
        private toastService: ToastService,
        private translateService: TranslateService,
        private discountsAndMultipliersService: DiscountsAndMultipliersService,
        private documentManagerService: DocumentManagerService,
        private offerValidityService: OfferValidityService,
        private taxRatesService: TaxRatesService,
        private priceBaseService: PriceBaseService2,
        private positionService: PositionService,
        private ownProductsService: OwnProductsService,
        private configuratorLoaderService: ConfiguratorLoaderService,
    ) {
        super();
        this.store
            .select(getSelectedSales)
            .pipe(
                tap((document) => {
                    this.openedDocument = document;
                }),
                distinctUntilChanged((a, b) => a?.uuid === b?.uuid)
            )
            .subscribe((document) => {
                if (this.configService.user?.id) {
                    this.discountsAndMultipliersService.loadDiscountsAndMultipliers(
                        this.configService.user?.user?.ActiveCompanyLicenseType === 'license' &&
                            document?.buyerContractorId
                    );
                    if (document?.type === 'sales' && document?.buyerCompanyId && this.activePriceVersion?.id) {
                        this.pricesDataService.loadPricesForContractor(
                            document.buyerCompanyId,
                            this.activePriceVersion?.id
                        );
                    }
                }
            });
        this.priceVersionService.activatedPriceVersion$.subscribe(async (priceVersion) => {
            if (
                priceVersion &&
                (!this.activePriceVersion || this.activePriceVersion.id !== priceVersion.id)
            ) {
                this.activePriceVersion = priceVersion;
                this.getPricesForPriceVersion();
            }
        });
    }

    async getPricesForPriceVersion() {
        this.priceUpdatePending = true;
        this.priceUpdatePending$.next(true);
        await this.pricesDataService.getProductPricesForPriceVersion(this.activePriceVersion?.id);
        if (this.openedDocument && this.openedDocument.buyerCompanyId) {
            await this.pricesDataService.loadPricesForContractor(
                this.openedDocument.buyerCompanyId,
                this.activePriceVersion?.id
            );
        }
        this.priceUpdatePending$.next(false);
        this.priceUpdatePending = false;
    }

    // eslint-disable-next-line max-statements
    async recalculatePosition(
        salesDocument: SalesDocument,
        specifiedPositions?: string[],
        showToasts = false,
        buy = false,
        force = false,
    ) {
        if (+this.configService.user.user.ActiveCompany === salesDocument.buyerCompanyId) {
            buy = true;
        }
        if (
            !this.priceUpdatePending &&
            this.pricesDataService.loadedPrices[this.activePriceVersion.id]
        ) {
            this.priceUpdatePending$.next(true);
            this.priceUpdatePending = true;
            if (!specifiedPositions?.length && showToasts) {
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Rozpoczęto przeliczanie cen'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Rozpoczęto przeliczanie cen dokumentu na ceny z aktualnej wersji cenowej.'
                    ),
                    severity: 'info',
                });
            }
            let positions = await this.documentManagerService.getDocumentPosition(
                salesDocument.uuid
            ) as SalesDocumentPosition[];
            if (specifiedPositions?.length) {
                positions = positions?.filter((pos) => specifiedPositions?.includes(pos?.uuid));
            }

            let oldestPriceCalculationDate = new Date();
            for (const position of positions) {
                if (position.priceCalculationDate < oldestPriceCalculationDate) {
                    oldestPriceCalculationDate = position.priceCalculationDate;
                }
            }

            // jeśli od obliczenia ceny najstarszej pozycji nie minęło ponad 60 minut to nie przeliczamy
            if (!force && !showToasts && oldestPriceCalculationDate && oldestPriceCalculationDate > addMinutes(new Date(), -60)) {
                this.priceUpdatePending$.next(false);
                this.priceUpdatePending = false;
                return false;
            }
            await this.getPricesForPriceVersion();
            await this.discountsAndMultipliersService.loadDiscountsAndMultipliers(
                this.configService.user.user.ActiveCompanyLicenseType === 'license' &&
                    salesDocument.buyerContractorId
            );
            let otherUnits = false;
            await this.pricesDataService.loadPricesForContractor(
                salesDocument.buyerCompanyId,
                this.activePriceVersion?.id
            );
            await this.pricesDataService.loadPricesForContractor(
                salesDocument.buyerCompanyId,
                salesDocument?.priceVersionId
            );
            const actualPrices = await this.pricesDataService.getProductPricesForPriceVersion(
                this.activePriceVersion.id,
                salesDocument.buyerCompanyId
            );
            const documentVersionPrices =
                await this.pricesDataService.getProductPricesForPriceVersion(
                    salesDocument?.priceVersionId,
                    salesDocument.buyerCompanyId
                );
            const ownProducts = await this.ownProductsService.loadOwnProducts();
            let notExitingOwnProducts = false;
            for (const position of positions) {
                if (
                    (position.customPriceValue && parseDate(position.validityDate) >= new Date() && position.type !== 'additional') ||
                    position.externalSource === 'ERP' ||
                    (position.type !== 'additional' && salesDocument.isConfirmedOnlySalesPositions)
                ) {
                    continue;
                }
                const priceCalculationDate = new Date();
                const currentOfferValidity =
                    this.offerValidityService.getCurrentOfferValidity(salesDocument);
                const documentPriceVersion = this.priceVersionService.getPriceVersion(
                    salesDocument.priceVersionId
                );
                const validityDate = this.positionService.getPositionValidity(
                    priceCalculationDate,
                    salesDocument.isConfirmed,
                    documentPriceVersion,
                    currentOfferValidity
                );
                if (position.type === 'static') {
                    position.configuration = core.parseJson(position.configuration);
                    const price = actualPrices?.[position.configuration?.productId];
                    let unitPrice = price?.priceUnits?.find(
                        (el) => Number(el.id) === Number(position.priceUnitId ?? 0) && el.code === position.priceUnitCode
                    );
                    const documentUnit = documentVersionPrices[
                        position.configuration?.productId
                    ]?.priceUnits?.find(
                        (item) => Number(item.id) === Number(position.priceUnitId ?? 0) && item.code === position.priceUnitCode
                    );
                    const activeUnitIds = price?.priceUnits?.map((item) => item?.id ?? 0);
                    if (!unitPrice && Object.keys(position?.masters || {}).length > 0) {
                        unitPrice = price?.priceUnits?.find(
                            (item) => item?.isPrimary,
                        );
                    }
                    if (unitPrice) {
                        const priceValue = unitPrice?.value;
                        let changedConfigurationUnit = false;
                        let changedUnit = false;
                        if (unitPrice.id !== position.priceUnitId || unitPrice.code !== position.priceUnitCode) {
                            position.priceUnitId = unitPrice?.id;
                            position.priceUnitCode = unitPrice?.code;
                            changedUnit = true;
                            if (!position.configuration?.unit?.symbol && unitPrice?.symbol) {
                                const unitInfo = this.pricesDataService.getUnitInfo(
                                    unitPrice.id,
                                    unitPrice.code
                                );
                                position.configuration.unit = {
                                    id: unitPrice.id,
                                    code: unitPrice.code,
                                    symbol: unitInfo.symbol,
                                    factorDesc: null,
                                    name: unitInfo.name,
                                };
                                changedConfigurationUnit = true;
                            }
                        }
                        if (buy) {
                            position.basePriceValue = priceValue;
                            position.basePriceCurrency = price.currency;
                            position.basePriceTaxRate = price.taxRate;
                            position.basePriceTaxRateId = this.taxRatesService.matchTaxRate(
                                price.taxRate
                            )?.id;
                            position.basePriceIsGross = price.isGross;
                        } else {
                            position.baseBuyPriceValue = priceValue;
                            position.baseBuyPriceCurrency = price.currency;
                            position.baseBuyPriceTaxRate = price.taxRate;
                            position.baseBuyPriceTaxRateId = this.taxRatesService.matchTaxRate(
                                price.taxRate
                            )?.id;
                            position.baseBuyPriceIsGross = price.isGross;
                            position.basePriceValue =
                                this.discountsAndMultipliersService.getPositionSalesPrice(
                                    priceValue,
                                    position
                                );
                            position.basePriceCurrency = price.currency;
                            position.basePriceTaxRate = price.taxRate;
                            position.basePriceTaxRateId = this.taxRatesService.matchTaxRate(
                                price.taxRate
                            )?.id;
                            position.basePriceIsGross = price.isGross;
                        }
                        if (!salesDocument.isOneTaxRateMode && !price.resetTaxRate && position.priceIsGross) {
                            position.priceTaxRateId = position.basePriceTaxRateId;
                            position.priceTaxRate = position.basePriceTaxRate;
                        }
                        await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
                            salesDocument,
                            position,
                            buy ? 'buy' : 'sales'
                        );
                        if (changedUnit) {
                            this.store.dispatch(
                                changePosition({
                                    id: position.uuid,
                                    docId: salesDocument.uuid,
                                    changes: {
                                        priceUnitId: position.priceUnitId,
                                        priceUnitCode: position.priceUnitCode,
                                    },
                                })
                            );
                        }

                        if (changedConfigurationUnit) {
                            this.store.dispatch(
                                changePosition({
                                    id: position.uuid,
                                    docId: salesDocument.uuid,
                                    changes: {
                                        configuration: position.configuration,
                                    },
                                })
                            );
                        }

                        this.store.dispatch(
                            changePositionBasePrice({
                                id: position.uuid,
                                docId: salesDocument.uuid,
                                changes: {
                                    basePriceValue: position.basePriceValue,
                                    basePriceCurrency: position.basePriceCurrency,
                                    basePriceIsGross: position.basePriceIsGross ?? false,
                                    basePriceTaxRate: position.basePriceTaxRate ?? null,
                                    basePriceTaxRateId: position?.basePriceTaxRateId ?? null,
                                    baseBuyPriceValue: position.baseBuyPriceValue ?? undefined,
                                    baseBuyPriceCurrency:
                                        position.baseBuyPriceCurrency ?? undefined,
                                    baseBuyPriceIsGross: position.baseBuyPriceIsGross ?? undefined,
                                    baseBuyPriceTaxRate: position.baseBuyPriceTaxRate ?? undefined,
                                    baseBuyPriceTaxRateId:
                                        position?.baseBuyPriceTaxRateId ?? undefined,
                                },
                            })
                        );
                        this.store.dispatch(
                            changePositionPrice({
                                id: position.uuid,
                                docId: salesDocument.uuid,
                                changes: {
                                    priceTaxRateId: position.priceTaxRateId,
                                    priceTaxRate: position.priceTaxRate,
                                    priceBeforePromotionsValue: position.priceBeforePromotionsValue,
                                    pricePromotions: position.pricePromotions,
                                    priceBeforeDiscountsValue: position.priceBeforeDiscountsValue,
                                    priceAfterDiscountsValue: position.priceAfterDiscountsValue,
                                    priceDiscounts: position.priceDiscounts,
                                    priceCurrency: position.priceCurrency,
                                    priceIsGross: position.priceIsGross,
                                    priceCalculationDate,
                                    validityDate,
                                },
                            })
                        );
                    } else if (activeUnitIds?.length) {
                        otherUnits = true;
                        this.store.dispatch(
                            changePosition({
                                id: position.uuid,
                                docId: salesDocument.uuid,
                                changes: {
                                    isInvalid: true,
                                    errors: JSON.stringify([
                                        {
                                            message: this.translateService.instant(
                                                'SALES_DOCUMENT|Nieprawidłowa jednostka pozycji.'
                                            ),
                                            type: 'BadUnit',
                                            isShown: true,
                                        },
                                    ]),
                                    hasMatchingPrices: activeUnitIds?.length,
                                    priceCalculationDate,
                                    validityDate,
                                },
                            })
                        );
                    }
                } else if (position.type === 'additional') {
                    if (!position.customPriceValue) {
                        position.configuration = core.parseJson(position.configuration);
                        const ownProduct = ownProducts.find(
                            (el) => el.id === position.configuration.productId
                        );
                        const ownProductPrice = ownProduct?.price;
                        position.baseBuyPriceValue = ownProductPrice ? Number(ownProductPrice?.value) : null;
                        if (ownProductPrice) {
                            position.baseBuyPriceCurrency = <string>ownProductPrice.currency;
                            position.baseBuyPriceTaxRate = ownProductPrice.taxRate;
                            position.baseBuyPriceTaxRateId = this.taxRatesService.matchTaxRate(
                                ownProductPrice.taxRate
                            )?.id;
                            position.baseBuyPriceIsGross = ownProductPrice.isGross;
                        }
                        position.basePriceValue =
                            ownProductPrice ?
                            this.discountsAndMultipliersService.getPositionSalesPrice(
                                Number(ownProductPrice?.value),
                                position
                            ) : null;
                        if (ownProductPrice) {
                            position.basePriceCurrency = <string>ownProductPrice.currency;
                            position.basePriceTaxRate = ownProductPrice.taxRate;
                            position.basePriceTaxRateId = this.taxRatesService.matchTaxRate(
                                ownProductPrice.taxRate
                            )?.id;
                            position.basePriceIsGross = ownProductPrice.isGross;
                        }
                        position.priceBeforePromotionsValue = position.basePriceValue;
                        position.priceBeforeDiscountsValue = position.basePriceValue;
                        if (ownProductPrice && !salesDocument.isOneTaxRateMode && !ownProduct.price.resetTaxRate && position.priceIsGross) {
                            position.priceTaxRateId = position.basePriceTaxRateId;
                            position.priceTaxRate = position.basePriceTaxRate;
                        }
                        await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
                            salesDocument,
                            position,
                            buy ? 'buy' : 'sales'
                        );
                        this.store.dispatch(
                            changePositionBasePrice({
                                id: position.uuid,
                                docId: salesDocument.uuid,
                                changes: {
                                    basePriceValue: position.basePriceValue,
                                    basePriceCurrency: position.basePriceCurrency,
                                    basePriceIsGross: position.basePriceIsGross ?? false,
                                    basePriceTaxRate: position.basePriceTaxRate ?? null,
                                    basePriceTaxRateId: position?.basePriceTaxRateId ?? null,
                                    baseBuyPriceValue: position.baseBuyPriceValue ?? undefined,
                                    baseBuyPriceCurrency:
                                        position.baseBuyPriceCurrency ?? undefined,
                                    baseBuyPriceIsGross: position.baseBuyPriceIsGross ?? undefined,
                                    baseBuyPriceTaxRate: position.baseBuyPriceTaxRate ?? undefined,
                                    baseBuyPriceTaxRateId:
                                        position?.baseBuyPriceTaxRateId ?? undefined,
                                },
                            })
                        );
                        this.store.dispatch(
                            changePositionPrice({
                                id: position.uuid,
                                docId: salesDocument.uuid,
                                changes: {
                                    priceTaxRateId: position.priceTaxRateId,
                                    priceTaxRate: position.priceTaxRate,
                                    priceBeforePromotionsValue: position.priceBeforePromotionsValue,
                                    pricePromotions: position.pricePromotions,
                                    priceBeforeDiscountsValue: position.priceBeforeDiscountsValue,
                                    priceAfterDiscountsValue: position.priceAfterDiscountsValue,
                                    priceDiscounts: position.priceDiscounts,
                                    priceCurrency: position.priceCurrency,
                                    priceIsGross: position.priceIsGross,
                                    priceCalculationDate,
                                    validityDate,
                                },
                            })
                        );

                        if (!ownProductPrice) {
                            notExitingOwnProducts = true;
                        }
                    } else {
                        this.store.dispatch(
                            changePositionPrice({
                                id: position.uuid,
                                docId: salesDocument.uuid,
                                changes: {
                                    priceCalculationDate,
                                    validityDate,
                                },
                            })
                        );
                    }
                } else {
                    await this.pricesDataService.loadPricesForConfiguratorAndPriceVersion(
                        this.activePriceVersion.id,
                        position.configuration?.type === 'complex'
                            ? position.configuration?.configuratorId
                            : position.configuration?.type
                    );
                    position.configuration = core.parseJson(position.configuration);
                    const oldConfiguration = core.copy(position.configuration);
                    const defaultTaxRate = this.taxRatesService.getDefaultTaxRate();
                    const defaultBuyTaxRate = this.taxRatesService.getBuyDefaultTaxRate();
                    const prices = {
                        taxRate: defaultTaxRate?.value ? defaultTaxRate.value : 0,
                        isGross: TaxRatesService.isGross(
                            this.taxRatesService.getTaxRateCalculationMethod()
                        ),
                        charge: this.discountsAndMultipliersService.charge,
                        multiplier:
                            this.discountsAndMultipliersService.getPositionMultiplier(position),
                        marketFactor: this.discountsAndMultipliersService.marketFactor,
                        priceVersionId: this.activePriceVersion.id,
                        contractorCompanyId: salesDocument.buyerCompanyId,
                    };
                    if (position.configuration?.type === 'complex') {
                        await this.runConfiguratorOfferPriceRecalculation(position);
                    }
                    this.priceBaseService.setPrice(position.configuration, prices);
                    position.configuration.price = position.configuration.Price;
                    position.configuration.priceNoMargin =
                        position.configuration.PriceNoMargin || position.configuration.Price;
                    position.configuration.priceAfterMultiplier =
                        position.configuration.PriceAfterMultiplier || position.configuration.Price;
                    position.configuration.priceAfterMultiplierNoMargin =
                        position.configuration.PriceAfterMultiplierNoMargin ||
                        position.configuration.PriceNoMargin;
                    position.configuration.priceAfterDiscounts =
                        position.configuration.PriceAfterDiscounts || position.configuration.Price;
                    position.configuration.priceIsGross =
                        position.configuration.PriceIsGross || false;
                    position.configuration.priceTaxRate = position.configuration.PriceTaxRate || 0;
                    if (position.configuration.Quantity) {
                        position.configuration.quantity = position.configuration.Quantity;
                    }
                    position.configuration.priceSegments = position.configuration.PriceSegments;
                    position.configuration.priceSegmentsNoMargin =
                        position.configuration.PriceSegmentsNoMargin;
                    position.configuration.priceParts = position.configuration.PriceParts;
                    position.configuration.pricePartsNoMargin =
                        position.configuration.PricePartsNoMargin;
                    if (position.configuration.DiscountGroups) {
                        position.configuration.discountGroups =
                            position.configuration.DiscountGroups;
                    }
                    if (position.configuration.DiscountGroupsNoMargin) {
                        position.configuration.discountGroupsNoMargin =
                            position.configuration.DiscountGroupsNoMargin;
                    }
                    const configurationTaxRate = this.taxRatesService.matchTaxRate(
                        position.configuration.priceTaxRate
                    );
                    const changedTaxRate =
                        configurationTaxRate?.value !== position.configuration.priceTaxRate;
                    if (buy) {
                        position.basePriceValue = TaxRatesService.recalcPrice(
                            position.configuration.PriceIsGross,
                            position.configuration.PriceNoMargin,
                            position.configuration.PriceIsGross,
                            position.configuration.priceTaxRate,
                            defaultBuyTaxRate?.value
                        );
                        position.basePriceTaxRate = defaultBuyTaxRate?.value;
                        position.basePriceTaxRateId = defaultBuyTaxRate.id;
                        position.basePriceIsGross = position.configuration.PriceIsGross;
                    } else {
                        position.baseBuyPriceTaxRate = defaultBuyTaxRate?.value;
                        position.baseBuyPriceValue = TaxRatesService.recalcPrice(
                            position.configuration.PriceIsGross,
                            position.configuration.PriceNoMargin,
                            position.configuration.PriceIsGross,
                            position.configuration.priceTaxRate,
                            defaultBuyTaxRate?.value
                        );
                        position.baseBuyPriceTaxRateId = defaultBuyTaxRate.id;
                        position.baseBuyPriceIsGross = position.configuration.PriceIsGross;
                        position.basePriceValue = position.configuration.Price;
                        position.basePriceTaxRate =
                            (changedTaxRate
                                ? configurationTaxRate?.value
                                : position.configuration.PriceTaxRate) ?? defaultTaxRate.value;
                        position.basePriceTaxRateId = configurationTaxRate?.id ?? defaultTaxRate.id;
                        position.basePriceIsGross = position.configuration.PriceIsGross;
                    }
                    position.priceBeforePromotionsValue = position.basePriceValue;
                    position.priceBeforeDiscountsValue = position.basePriceValue;
                    if (!salesDocument.isCustomTaxRatesMode) {
                        position.priceTaxRateId = position.basePriceTaxRateId;
                        position.priceTaxRate = position.basePriceTaxRate;
                    }
                    await this.discountsAndMultipliersService.calculateDiscountsAndMultipliers(
                        salesDocument,
                        position,
                        buy ? 'buy' : 'sales'
                    );
                    if (!deepCompare(oldConfiguration, position.configuration, new Set([
                        'PriceParts',
                        'PricePartsNoMargin',
                        'PriceSegments',
                        'PriceSegmentsNoMargin',
                        'Price',
                        'PriceNoMargin',
                        'PriceAfterMultiplier',
                        'PriceAfterDiscounts',
                        'PriceIsGross',
                        'PriceTaxRate',
                        'Quantity',
                        'PriceElemsNoMargin',
                        'PriceElems',
                        'NoPriceElems',
                        'NoPriceCauses',
                        'PriceValid',
                    ]))) {
                        this.store.dispatch(
                            changePosition({
                                id: position.uuid,
                                docId: salesDocument.uuid,
                                changes: {
                                    configuration: position.configuration,
                                },
                            })
                        );
                    }
                    this.store.dispatch(
                        changePositionBasePrice({
                            id: position.uuid,
                            docId: salesDocument.uuid,
                            changes: {
                                baseBuyPriceTaxRateId: position.baseBuyPriceTaxRateId,
                                baseBuyPriceTaxRate: position.baseBuyPriceTaxRate,
                                baseBuyPriceIsGross: position.baseBuyPriceIsGross,
                                baseBuyPriceValue: position.baseBuyPriceValue ?? undefined,
                                basePriceTaxRateId: position.basePriceTaxRateId,
                                basePriceTaxRate: position.basePriceTaxRate,
                                basePriceIsGross: position.basePriceIsGross,
                                basePriceValue: position.basePriceValue,
                            },
                        })
                    );
                    this.store.dispatch(
                        changePositionPrice({
                            id: position.uuid,
                            docId: salesDocument.uuid,
                            changes: {
                                priceTaxRateId: position.priceTaxRateId,
                                priceTaxRate: position.priceTaxRate,
                                priceBeforePromotionsValue: position.priceBeforePromotionsValue,
                                pricePromotions: position.pricePromotions,
                                priceBeforeDiscountsValue: position.priceBeforeDiscountsValue,
                                priceAfterDiscountsValue: position.priceAfterDiscountsValue,
                                priceDiscounts: position.priceDiscounts,
                                priceCurrency: position.priceCurrency,
                                priceIsGross: position.priceIsGross,
                                priceCalculationDate,
                                validityDate,
                            },
                        })
                    );
                }
            }
            if (notExitingOwnProducts && showToasts) {
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Niepoprawne pozycje dodatkowe'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Niektóre pozycje dodatkowe w dokumencie nie mają przypisanego produktu własnego, w związku z czym nie mają ustalonej ceny. Należy ręcznie ustawić cenę tym pozycjom lub je usunąć.'
                    ),
                    severity: 'error',
                });
            }
            if (!otherUnits && !specifiedPositions?.length && showToasts) {
                this.documentManagerService.saveDocument(salesDocument.uuid, {
                    priceVersionId: Number(this.activePriceVersion?.id),
                });
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Zakończono przeliczanie cen'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Przeliczanie cen powiodło się. Ceny pozycji w dokumencie zostały zmienione na dostępne w aktywnej wersji cenowej'
                    ),
                    severity: 'success',
                });
            } else if (!specifiedPositions?.length && showToasts) {
                this.toastService.add({
                    title: this.translateService.instant(
                        'SALES_DOCUMENT|Nieprawidłowe jednostki w pozycjach'
                    ),
                    body: this.translateService.instant(
                        'SALES_DOCUMENT|Dokument zawiera pozycje wycenione w jednostkach niedostępnych w nowej wersji cenowej. Aby dokończyć przeliczanie cen, należy manualnie zmienić jednostki w wyszczególnionych pozycjach'
                    ),
                    severity: 'error',
                });
            }
            this.priceUpdatePending$.next(false);
            this.priceUpdatePending = false;
            return otherUnits;
        }
    }

    private async runConfiguratorOfferPriceRecalculation(position: SalesDocumentPosition) {
        if (!this.configuratorLoaderService.configuratorsRepositoryServiceStatic) {
            return false;
        }
        try {
            const configuratorId = position.configuration?.configuratorId ?? 0;
            await this.configuratorLoaderService.configuratorsRepositoryService.loadConfigurator(
                position.type,
                configuratorId
            );
            await this.configuratorLoaderService.configuratorsRepositoryServiceStatic
                .getOfferPriceRecalculation(position.type, configuratorId)
                ?.execute(position.configuration);
        } catch (error) {
            console.error(error);
        }
    }
}
